import {
  Box,
  FormControl,
  FormLabel,
  Grid,
  Input,
  useColorModeValue,
} from "@chakra-ui/react";
import {
  AudiencePricingStructure,
  AudiencePricingStructureValue,
  DSPValue,
} from "@intentsify/types";
import { Button, Select } from "components";
import {
  Control,
  Controller,
  UseFieldArrayRemove,
  UseFormRegister,
  UseFormSetValue,
} from "react-hook-form";
import {
  DSPOptions,
  pricingStructureOptions,
} from "../screens/CreateAudience/utils/selectValues";
import { liverampColor } from "../utils/utils";
import { AudienceFormInput, AudienceFormState } from "./AudienceForm";

type Props = {
  index: number;
  register: UseFormRegister<AudienceFormInput>;
  remove: UseFieldArrayRemove;
  control: Control<AudienceFormInput, unknown, AudienceFormState>;
  digitalAdTargeting: boolean;
  setValue: UseFormSetValue<AudienceFormInput>;
};

export const ActivationDestinationFieldSet = ({
  index,
  register,
  remove,
  control,
  digitalAdTargeting,
  setValue,
}: Props) => {
  return (
    <Box
      border="1px solid"
      borderColor={useColorModeValue("gray.200", "gray.700")}
      borderRadius="md"
      mt={4}
      w={1400}
      p={8}
      mb={2}
    >
      <Grid templateColumns="repeat(3, 1fr)" gap={6}>
        <FormControl isRequired>
          <FormLabel color={liverampColor} fontSize="sm" fontWeight="bold">
            Destination
          </FormLabel>
          <Controller
            name={`activationDestinations.${index}.dsp`}
            control={control}
            render={({ field }) => (
              <Select
                size="sm"
                isMulti={false}
                isClearable={false}
                allowMultilineOptions={true}
                placeholder="Select a DSP"
                options={DSPOptions}
                value={DSPOptions.find((c) => c.value === field.value)}
                onChange={(opt) => {
                  if (!opt || !opt.value) {
                    return;
                  }

                  setValue(
                    `activationDestinations.${index}.dsp`,
                    opt.value as DSPValue,
                  );
                }}
              />
            )}
          />
        </FormControl>

        <FormControl w="400px" isRequired={digitalAdTargeting}>
          <FormLabel color={liverampColor} fontSize="sm" fontWeight="bold">
            Digital Ad Targeting Price (CPM)
          </FormLabel>
          <Input
            {...register(`activationDestinations.${index}.digitalAdCpm`)}
            type="text"
            placeholder=""
            shadow="sm"
            size="sm"
            rounded="md"
          />
        </FormControl>

        <FormControl w="400px">
          <FormLabel color={liverampColor} fontSize="sm" fontWeight="bold">
            Content Marketing Price (CPM)
          </FormLabel>
          <Input
            {...register(`activationDestinations.${index}.contentMarketingCpm`)}
            type="text"
            placeholder=""
            shadow="sm"
            size="sm"
            rounded="md"
          />
        </FormControl>

        <FormControl w="400px">
          <FormLabel color={liverampColor} fontSize="sm" fontWeight="bold">
            TV Targeting Price (CPM)
          </FormLabel>
          <Input
            {...register(`activationDestinations.${index}.tvTargetingCpm`)}
            type="text"
            placeholder=""
            shadow="sm"
            size="sm"
            rounded="md"
          />
        </FormControl>

        <FormControl w="400px">
          <FormLabel color={liverampColor} fontSize="sm" fontWeight="bold">
            Cost Per Click
          </FormLabel>
          <Input
            {...register(`activationDestinations.${index}.cpm`)}
            type="text"
            placeholder=""
            shadow="sm"
            size="sm"
            rounded="md"
          />
        </FormControl>

        <FormControl w="400px">
          <FormLabel color={liverampColor} fontSize="sm" fontWeight="bold">
            Programmatic % of Media
          </FormLabel>
          <Input
            {...register(`activationDestinations.${index}.programmaticPercent`)}
            type="text"
            placeholder=""
            shadow="sm"
            size="sm"
            rounded="md"
          />
        </FormControl>

        <FormControl w="400px">
          <FormLabel color={liverampColor} fontSize="sm" fontWeight="bold">
            CPM Cap
          </FormLabel>
          <Input
            {...register(`activationDestinations.${index}.cpmCap`)}
            type="text"
            placeholder=""
            shadow="sm"
            size="sm"
            rounded="md"
          />
        </FormControl>
        <FormControl w="400px">
          <FormLabel color={liverampColor} fontSize="sm" fontWeight="bold">
            Advertiser Direct % of Media
          </FormLabel>
          <Input
            {...register(
              `activationDestinations.${index}.advertiserDirectPercent`,
            )}
            type="text"
            placeholder=""
            shadow="sm"
            size="sm"
            rounded="md"
          />
        </FormControl>
        <FormControl isRequired>
          <FormLabel color={liverampColor} fontSize="sm" fontWeight="bold">
            Pricing Structure
          </FormLabel>
          <Controller
            name={`activationDestinations.${index}.pricingStructure`}
            control={control}
            render={({ field }) => (
              <Select
                size="sm"
                isMulti={true}
                isClearable={false}
                allowMultilineOptions={true}
                placeholder="Select a pricing structure"
                options={pricingStructureOptions}
                value={pricingStructureOptions.filter((c) => {
                  const enumValue =
                    AudiencePricingStructure[
                      c.value as AudiencePricingStructureValue
                    ];

                  if (!field.value) {
                    return null;
                  }

                  return field.value.includes(enumValue);
                })}
                onChange={(options) => {
                  if (!options) {
                    return;
                  }

                  setValue(
                    `activationDestinations.${index}.pricingStructure`,
                    options.map(
                      (opt) => opt.value,
                    ) as AudiencePricingStructure[],
                  );
                }}
              />
            )}
          />
        </FormControl>
        <Button w={40} variant="warning" onClick={() => remove(index)}>
          Remove
        </Button>
      </Grid>
    </Box>
  );
};
